import { useState } from 'react';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ServicesSection from './components/ServicesSection';
import ServiceAreasSection from './components/ServiceAreasSection';
import WhyChooseUsSection from './components/WhyChooseUsSection';
import TestimonialsSection from './components/TestimonialsSection';
import FAQSection from './components/FAQSection';
import BlogSection from './components/BlogSection';
import Footer from './components/Footer';
import ContactModal from './components/ContactModal';
import SEOHead from '../../components/SEOHead';

export default function HomePage() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": `${siteUrl}/#organization`,
        "name": "Blue Team Carpet & Dryer Vent Cleaning",
        "alternateName": "Blue Team Home Services",
        "url": siteUrl,
        "logo": `${siteUrl}/vite.svg`,
        "image": "https://www.blueteamhomeservices.com/wp-content/uploads/2022/03/Home.jpg",
        "description": "Professional carpet cleaning, upholstery cleaning, and dryer vent cleaning services in Escondido and San Diego County since 1994. We use truck-mounted steam cleaning equipment and eco-friendly products safe for families and pets.",
        "telephone": "+16197482446",
        "priceRange": "$$",
        "foundingDate": "1994",
        "slogan": "Professional Cleaning Services You Can Trust Since 1994",
        "knowsAbout": [
          "Carpet Cleaning",
          "Dryer Vent Cleaning",
          "Upholstery Cleaning",
          "Steam Cleaning",
          "Pet Stain Removal",
          "Odor Removal",
          "Commercial Cleaning",
          "Residential Cleaning"
        ],
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Escondido",
          "addressRegion": "CA",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "33.1192",
          "longitude": "-117.0864"
        },
        "areaServed": [
          {
            "@type": "City",
            "name": "Escondido"
          },
          {
            "@type": "AdministrativeArea",
            "name": "San Diego County"
          }
        ],
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:00",
            "closes": "18:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "Saturday",
            "opens": "09:00",
            "closes": "17:00"
          }
        ],
        "sameAs": [
          "https://www.facebook.com/blueteamhomeservices",
          "https://www.instagram.com/blueteamhomeservices"
        ],
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "127"
        }
      },
      {
        "@type": "Service",
        "@id": `${siteUrl}/#service`,
        "serviceType": "Carpet Cleaning",
        "provider": {
          "@id": `${siteUrl}/#organization`
        },
        "areaServed": {
          "@type": "City",
          "name": "Escondido"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Cleaning Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Residential Carpet Cleaning",
                "description": "Professional steam cleaning for residential carpets using truck-mounted equipment and eco-friendly products. Removes dirt, stains, allergens, and odors. Safe for children and pets."
              },
              "price": "89.00",
              "priceCurrency": "USD"
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Furniture & Upholstery Cleaning",
                "description": "Professional steam cleaning for furniture and upholstery including sofas, chairs, and mattresses. Removes stains, odors, and allergens while protecting fabric integrity."
              },
              "price": "49.00",
              "priceCurrency": "USD"
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Dryer Vent Cleaning",
                "description": "Professional dryer vent cleaning services to prevent fire hazards, improve dryer efficiency, and reduce energy costs. Removes lint buildup and debris from vent systems."
              }
            }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "@id": `${siteUrl}/#faq`,
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How often should I steam clean my carpets or furniture?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "It all depends on how much you use it. If it's the carpeting in your home, we recommend to steam clean it twice a year. In high traffic areas such as an office, a restaurant, or any other commercial property, we recommend steam cleaning carpets at least 2-4 times a year. Furniture doesn't require steam cleaning as frequently. Once a year is enough for most home furniture and cleaning twice a year is recommended for furniture in a commercial environment."
            }
          },
          {
            "@type": "Question",
            "name": "How long does it take to steam clean carpets or furniture?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "It all depends on the size of the cleaning surface. For example, it takes about 1.5-2 hours to steam clean 1000 sq.ft. of carpeted area. It takes about 1 hour to steam clean a regular 2-3 seat sofa."
            }
          },
          {
            "@type": "Question",
            "name": "Is it safe to be around while steam cleaning?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, it's totally safe to be around while steam cleaning. We use only the best, environmentally friendly cleaning products that are absolutely allergy free and safe for people and pets."
            }
          },
          {
            "@type": "Question",
            "name": "Can you take away urine smell?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, we can remove urine smell by using biological treatment cleaning product. This is biologically enhanced hard surface cleaner, that utilizes special bacterial cultures, containing the ideal synergized blend of selectively adapted micro-organisms."
            }
          },
          {
            "@type": "Question",
            "name": "How much does carpet cleaning cost?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Pricing starts at $99 for carpet cleaning and varies depending on the number of rooms with carpet that require cleaning. Area rug cleaning starts at $1.00/square ft."
            }
          },
          {
            "@type": "Question",
            "name": "When can I walk on my carpets after steam cleaning?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We recommend waiting 4-5 hours before resuming regular activities in your carpeted areas. If you need to access the space, you should ensure that your feet are clean so as not to introduce any dirt and risk staining your freshly cleaned carpets."
            }
          }
        ]
      },
      {
        "@type": "WebPage",
        "@id": `${siteUrl}/#webpage`,
        "url": siteUrl,
        "name": "Escondido Carpet Cleaning | Dryer Vent Cleaning | Blue Team",
        "description": "Blue Team offers professional carpet cleaning, upholstery cleaning, and dryer vent cleaning in Escondido and San Diego County since 1994.",
        "isPartOf": {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": siteUrl,
          "name": "Blue Team Carpet & Dryer Vent Cleaning"
        },
        "about": {
          "@id": `${siteUrl}/#organization`
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "url": "https://www.blueteamhomeservices.com/wp-content/uploads/2022/03/Home.jpg"
        }
      }
    ]
  };

  return (
    <>
      <SEOHead schema={schemaData} />
      <div className="min-h-screen bg-white">
        <Header onContactClick={() => setIsContactModalOpen(true)} />
        <main>
          <HeroSection onContactClick={() => setIsContactModalOpen(true)} />
          <AboutSection />
          <ServicesSection onContactClick={() => setIsContactModalOpen(true)} />
          <ServiceAreasSection onContactClick={() => setIsContactModalOpen(true)} />
          <WhyChooseUsSection />
          <TestimonialsSection />
          <FAQSection />
          <BlogSection />
        </main>
        <Footer />
        <ContactModal 
          isOpen={isContactModalOpen} 
          onClose={() => setIsContactModalOpen(false)} 
        />
      </div>
    </>
  );
}
